/**
 * Evaluates a sorting value and returns a corresponding numerical representation.
 * If the value is not provided, returns undefined.
 * If the value is 'asc', returns -1.
 * If the value is 'desc', returns 1.
 *
 * @param {string} value - The sorting value to evaluate ('asc' or 'desc').
 * @returns {number | undefined} - Returns -1 for 'asc', 1 for 'desc', or undefined if value is not provided.
 */
export const evaluateSortValue = (value) => {
  if (!value) {
    return undefined;
  }

  if (value === 'asc') {
    return -1;
  }

  if (value === 'desc') {
    return 1;
  }

  // If it's neither 'asc' nor 'desc', return undefined
  return undefined;
};

/**
 * Extract specific keys and their values from an object.
 *
 * @param {Object} sourceObject - The object to extract values from.
 * @param {Array<string>} keysToExtract - An array of keys that should be extracted.
 * @returns {Object} - A new object containing only the specified keys and their corresponding values from the source object.
 *
 * @example
 * const source = { a: 1, b: 2, c: undefined, d: 4 };
 * const keys = ['a', 'c', 'd'];
 * const result = extractDefinedKeys(source, keys);
 * console.info(result); // { a: 1, d: 4 }
 */
export function extractDefinedKeys(sourceObject, keysToExtract) {
  if (!Array.isArray(keysToExtract)) {
    return [];
  }

  return keysToExtract.reduce((result, key) => {
    if (sourceObject[key] !== undefined) {
      result[key] = sourceObject[key];
    }
    return result;
  }, {});
}

/**
 * Convert an array of strings into an object with keys from the array
 * and all values set to `undefined`.
 *
 * @param {Array<string>} keysArray - The array of strings to convert into an object.
 * @returns {Object} - An object where each key is from the array and its value is `undefined`.
 *
 * @example
 * const keys = ["sortCountry", "sortCity"];
 * const result = arrayToUndefinedObject(keys);
 * console.info(result); // { sortCountry: undefined, sortCity: undefined }
 */
export function arrayToUndefinedObject(keysArray) {
  if (!Array.isArray(keysArray)) {
    return [];
  }

  return keysArray.reduce((acc, key) => {
    acc[key] = undefined;
    return acc;
  }, {});
}
